import { Settings2, SlidersHorizontal } from 'lucide-react';
import { createElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSettingsSearch } from '@/hooks/useSettingsSearch';
import { jumpToSetting } from '@/lib/setting-jump';

import type { PaletteAction } from './CommandPalette';
import type { SidebarItem } from '@/components/Sidebar/Sidebar';

/**
 * The ⌘K entries for individual settings (T17), read from the shared index.
 *
 * Before this, the palette offered two commands and sidebar rows: typing
 * "retention", "anonymous visitors" or "forwarding" found nothing. That mattered
 * more after Phase D (ADR-0368), which deliberately spread the settings across
 * four surfaces — good for the operator who knows where they are, useless for the
 * one who doesn't.
 *
 * It is now a THIN consumer of `useSettingsSearch`. It used to own a private
 * reachability check and hand the palette bare labels, which is two ways for the
 * two search surfaces to disagree: the palette missed the Labs gate entirely
 * (offering the three `/experience` settings for a page the sidebar hides and
 * the router 404s), and it matched on labels alone, so the keyword bags the
 * top-bar field reads did nothing here.
 *
 * PAGE entries are dropped: the palette already lists the sidebar destinations
 * itself, and offering both would show every page twice.
 */
export function useSettingCommands(sidebarItems: readonly SidebarItem[]): PaletteAction[] {
  const navigate = useNavigate();
  const entries = useSettingsSearch(sidebarItems);

  return entries
    .filter((entry) => entry.kind !== 'page')
    .map((entry) => ({
      id: entry.id,
      label: entry.label,
      // The trail is what tells two similar names apart — "Language" under
      // Account preferences is not the reply-language control.
      trail: entry.breadcrumb,
      // Precomputed by the index, so the palette matches on keywords exactly as
      // the top-bar field does rather than on the label alone.
      haystack: entry.haystack,
      icon: createElement(entry.kind === 'control' ? SlidersHorizontal : Settings2, {
        className: 'h-4 w-4',
      }),
      run: () => {
        if (entry.settingKey === undefined) return;
        jumpToSetting(navigate, entry.settingKey, entry.controlId);
      },
    }));
}
